<template>
  <div class="body d-flex py-lg-3 py-md-2">
    <div class="container-xxl">
      <div class="card wx-report-head mb-3">
        <div class="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div class="wx-report-code">{{ project?.info?.code }}</div>
            <h3 class="wx-report-title">{{ project?.info.description }}</h3>
          </div>
          <div class="wx-report-meta" v-if="project?.info">
            <div><span>Start date</span><b>{{ project.info?.startDate }}</b></div>
            <div><span>End date</span><b>{{ project.info?.endDate }}</b></div>
            <div><span>Estimated hours</span><b>{{ project.info?.estimatedTime }}</b></div>
          </div>
        </div>
      </div>

      <div class="card wx-report-filters mb-3">
        <div class="card-body d-flex flex-wrap align-items-center gap-2">
          <div class="wx-report-select permission-applied" v-if="superUser || canViewAll">
            <select2 v-model="filters.userId" :settings="userSettings" :options="userOptions" placeholder="Χρήστες"
              multiple></select2>
          </div>
          <div class="wx-report-select">
            <select2 v-model="filters.statusId" :settings="statusSettings" :options="statusOptions"
              placeholder="Status" multiple></select2>
          </div>
          <div class="d-flex text-nowrap align-items-center gap-2">
            <label for="filterDateFrom" class="form-label mb-0">Ημ/νία από:</label>
            <input type="date" id="filterDateFrom" class="form-control" v-model="filters.dateFrom">
          </div>
          <div class="d-flex text-nowrap align-items-center gap-2">
            <label for="filterDateTo" class="form-label mb-0">Ημ/νία έως:</label>
            <input type="date" id="filterDateTo" class="form-control" v-model="filters.dateTo">
          </div>
          <button type="button" class="btn wx-icon-btn ms-auto" @click="fetchData"><i
              class="icofont-search fs-6"></i>Search</button>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body table-responsive">
          <table id="reports" class="table table-hover align-middle mb-0 mis_table" style="width:100%">
            <thead>
              <tr>
                <th style="width:40%">Νame</th>
                <th style="width:30%">User Total</th>
                <th style="width:30%">Task Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="user in users" :key="user.userId">
                <tr @click="toggleTasks(user.userId)" style="cursor: pointer;">
                  <td><router-link :to="`/users?id=${user.userId}`">{{ user.username }}</router-link>
                  </td>
                  <td>{{ user.user_total }}</td>
                  <td>{{ user.project_total }}</td>
                </tr>
                <tr v-if="user.tasks && user.show_tasks" v-for="task in user.tasks"
                  :key="task.task_id" class="reports_tasks" style="font-weight: lighter;">
                  <td><router-link :to="`/task-report?id=${task.task_id}`">{{ task.title
                      }}</router-link></td>
                  <td>{{ task.user_total }}</td>
                  <td>{{ task.task_total_formatted }}</td>
                </tr>

              </template>
              <tr v-if="!users || !users.length">
                <td colspan="100">No data</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api';
import axios from 'axios';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { RouterLink } from 'vue-router';
import Select2 from 'vue3-select2-component';
import { mapGetters } from 'vuex';

export default {
  components: {
    Select2,
    RouterLink
  },
  data() {
    return {
      filters: {
        dateFrom: null,
        dateTo: null,
        userId: [],
        statusId: [],
      },
      dateFrom: null,
      dateTo: null,
      project: null,
      users: [],
      statuses: [],
      reports: [],
      showActionsDropdown: false,
    };
  },
  computed: {
    ...mapGetters({
      getUserPermission: 'getUserPermission',
      getSuperUserPermission: 'getSuperUserPermission',

    }),
    userSettings() {
      return { multiple: true, allowClear: true };
    },
    userOptions() {
      return this.users.map(user => ({ id: user.userId, text: user.username }));
    },
    statusSettings() {
      return { multiple: true, allowClear: true };
    },
    statusOptions() {
      return this.statuses.map(status => ({ id: status.id, text: status.description }));
    },
    superUser() {
      return this.getSuperUserPermission === 1;
    },
    userPermissions() {
      return this.getUserPermission('Reporting');
    },

    canViewAll() {
      return this.userPermissions?.viewAdmin === 1;
    },

  },
  methods: {
    transformDate(dateString) {
      // Split the input date string by the dash
      const [year, month, day] = dateString.split('-');

      // Return the formatted date as DD/MM/YYYY
      return `${day}/${month}/${year}`;
    },
    closeActionsDropdown(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.showActionsDropdown = false;
      }
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    getAllParams() {
      const params = new URLSearchParams(window.location.search);
      const allParams = {};

      // Iterate over each parameter
      for (const [key, value] of params.entries()) {
        if (key != 'id') {
          if (allParams[key]) {
            allParams[key] = [allParams[key]];
            allParams[key].push(value);
          } else {
            allParams[key] = value;
          }
        }
      }

      return allParams;
    },
    fetchData() {
      const { dateFrom, dateTo, userId, statusId } = this.filters;
      const projectId = this.$route.query.id;
      apiClient.get('get-project/?id=' + this.$route.query.id).then((response) => {
        if (response.data.success) {
          this.project = response.data.data;
        }
      }).catch((error) => {
        console.error('Error fetching data:', error);
      });
      apiClient
        .get('/get-hours-report-project/' + projectId, {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
            statusId: Array.isArray(statusId) ? statusId : [statusId],
            userId: Array.isArray(userId) ? userId : [userId]
          }
        })
        .then((response) => {
          if (response.data.success) {
            this.users = response.data.data.data.users.map(report => ({
              ...report,
              show_tasks: false // Ensure every report has this property
            }));
            this.statuses = response.data.data.data.project_statuses;
            this.reports.forEach(report => {
              report.tasks.forEach(task => {
                task.show = false;
              });
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    },
    exportWithoutTasks() {
      this.exportData(false);
    },
    exportWithTasks() {
      this.exportData(true);
    },
    exportData(withTasks) {
      const { dateFrom, dateTo, userId, statusId } = this.filters;
      apiClient
        .get('/get-hours-report', {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
            userId: Array.isArray(userId) ? userId : [userId],
            statusId: Array.isArray(statusId) ? statusId : [statusId],
            export: 1,
            export_with_tasks: withTasks ? 1 : undefined,
          },
          responseType: 'blob',
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `hours-export${withTasks ? '-with-tasks' : ''}.xlsx`);
          document.body.appendChild(link);
          link.click();
        })
        .catch((error) => {
          console.error('Error exporting data:', error);
        });
    },
    toggleTasks(userId) {
      const user = this.users.find((r) => r.userId === userId);
      user.show_tasks = !user.show_tasks;
    },
    getUserProjectHours(userHours, userId) {
      const user = userHours.find((u) => u.userId === userId);
      return user ? user.project_worked_hours_formatted : '0';
    },
    getUserTaskHours(userHours, userId) {
      const user = userHours.find((u) => u.userId === userId);
      return user ? user.task_worked_hours_formatted : '0';
    },
    getAuthToken() {
      // Implement your method to get the auth token
      return localStorage.getItem('api_token');
    },
  },
  mounted() {
    this.fetchData();
    document.addEventListener('click', this.closeActionsDropdown);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeActionsDropdown);
  },
};
</script>

<style scoped>
/* Add any scoped styles here */
.select2-selection__choice {
  background-color: #6C757D;
}
</style>